const _excluded = ["session", "authToken", "authFlowDone", "claims", "userInfo", "userClientData", "ff"];

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import * as Sentry from '@sentry/react';
import { Base64 } from 'js-base64';
import produce from 'immer';
import lodashGet from 'lodash/get';
import { handlePromise, handleRequest } from "../infra/utils";
import { logBiEvent } from "../event-reporter";
import getStoreApi from "./store-api";
import { defaultFeatureFlagsKey, getDefaultInitialState, getOrCreateStoreLocation } from "./store.utils";
import { selectors } from "./state-selectors";
import { defaultAgreementStatusFilter } from "../pages/AgreementsDashboard/agreementsDashboardFilters";
import isEqual from 'lodash/isEqual';
import * as jsonDiff from 'json-diff';
export const getStoreActions = (setState, getState, onLogout, getNow, getIntercom, slackClient) => {
  const api = getStoreApi(getState, onLogout, getNow, slackClient);
  const {
    sendRequest
  } = api;
  /** Resets the store state */

  function reset(authFlowDone) {
    setState(getDefaultInitialState(_objectSpread({}, getState().session), {
      authFlowDone
    }), false, 'reset');
  }
  /** Resets the business data. used when switching businesses  */


  function resetBusinessData() {
    const _getDefaultInitialSta = getDefaultInitialState(getState().session),
          {
      session: _1,
      authToken: _2,
      authFlowDone: _3,
      claims: _4,
      userInfo: _5,
      userClientData: _6,
      ff: _7
    } = _getDefaultInitialSta,
          resetData = _objectWithoutProperties(_getDefaultInitialSta, _excluded);

    setState(resetData, false, 'resetBusinessData');
  }
  /**
   * Starts a new request. baseLocation is the key in the store or an array that
   * represents a path in the store.
   */


  function requestStart(baseLocation) {
    setState(produce(state => {
      const loc = getOrCreateStoreLocation(state, baseLocation);
      loc.loading = true;
    }), false, "requestStart ".concat(JSON.stringify(baseLocation)));
  }
  /**
   * Ends a request successfully with response data. baseLocation is the key in the
   * store or an array that represents a path in the store.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any


  function requestSuccess(baseLocation, data) {
    setState(produce(state => {
      const loc = getOrCreateStoreLocation(state, baseLocation);
      loc.loading = false;
      loc.lastSuccessfulFetch = Date.now();
      loc.data = data;
      loc.error = null;
    }), false, "requestSuccess ".concat(JSON.stringify(baseLocation)));
  }
  /**
   * Ends a request with an error. baseLocation is the key in the store or an array that
   * represents a path in the store.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any


  function requestFail(baseLocation, error) {
    setState(produce(state => {
      const loc = getOrCreateStoreLocation(state, baseLocation);
      loc.loading = false;
      loc.data = null;
      loc.error = error;
    }), false, "requestFail ".concat(JSON.stringify(baseLocation)));
  }

  function setRelationshipScope(relationshipScope) {
    if (isEqual(relationshipScope, getState().relationshipScope)) {
      return;
    }

    if (!selectors.impersonatingAs(getState())) {
      getIntercom()('boot', {
        relationship_id: relationshipScope && relationshipScope.id
      });
    }

    setState({
      relationshipScope
    }, false, 'relationshipScope');
  }

  function setRelationshipVendorId(id) {
    setState({
      ff: _objectSpread(_objectSpread({}, getState().ff), {}, {
        relationshipVendorId: id
      })
    }, false, 'setRelationshipVendorId');
  }
  /**
   * Fetches the feature-flags and saves in the store. If given a businessId will fetch
   * the feature-flags for that businessId. Otherwise, will fetch global feature-flags.
   */


  async function fetchFeatureFlags(businessId, authToken) {
    const storeLocation = ['ff', 'featureFlags', businessId || defaultFeatureFlagsKey];
    const featureFlags = lodashGet(getState(), storeLocation);

    if (featureFlags && (featureFlags.loading || featureFlags.data)) {
      // No need to load twice if data exists or a request is already in progress.
      return;
    }

    requestStart(storeLocation);

    try {
      const featureFlags = await api.featureFlag.getAll(businessId, authToken);
      requestSuccess(storeLocation, featureFlags);
    } catch (err) {
      requestFail(storeLocation, err);
    }
  }

  async function setAuthToken(authToken) {
    if (authToken == null) {
      reset();
      await fetchFeatureFlags();
      setState({
        authFlowDone: true
      });
      return;
    }

    const payload = authToken.split('.', 3)[1];
    const claims = payload ? JSON.parse(Base64.decode(payload)) : null;

    if (selectors.isResetPasswordFlow({
      claims
    })) {
      await fetchFeatureFlags(selectors.user.businessId(getState()), authToken);
      setState({
        authToken,
        claims,
        authFlowDone: true
      });
      return;
    }

    try {
      setState({
        claims,
        authToken
      });
      const userInfo = await api.auth.getUserInfo();

      if (selectors.user.isVendor({
        userInfo
      })) {
        // We need to fetchIntegrations before setting userInfo to prevent a race condition where pages
        // are loaded before integrations are available when logging in to a concrete page that needs QBO data.
        await fetchIntegrations();
      }

      setUserInfo(userInfo);

      if (userInfo == null) {
        reset();
        await fetchFeatureFlags();
        setState({
          authFlowDone: true
        });
        return;
      }

      try {
        const businessId = selectors.user.businessId(getState());
        await Promise.all([fetchFeatureFlags(businessId), fetchUserClientData(), fetchBusinessClientData(businessId)]);
      } catch (err) {
        console.error('Error: failed to read userClientData', err);
        return;
      }
    } catch (err) {
      console.error('Error: failed to read user info');
      reset();
      throw err;
    } finally {
      setState({
        authFlowDone: true
      });
    }
  }

  async function fetchUserInfo() {
    const userInfo = await api.auth.getUserInfo();
    setUserInfo(userInfo);
    return userInfo;
  }

  function setUserInfo(userInfo) {
    setState({
      userInfo
    }, false, 'setUserInfo');
  }

  function setWorksAt(worksAt) {
    setState(state => ({
      userInfo: _objectSpread(_objectSpread({}, state.userInfo || {}), {}, {
        worksAt
      })
    }), false, 'setWorksAt');
  }

  function setUserClientData(userClientData) {
    setState({
      userClientData
    }, false, 'setUserClientData');
  }

  function setBusinessClientData(businessId, data) {
    setState({
      businessClientData: _objectSpread(_objectSpread({}, getState().businessClientData), {}, {
        [businessId]: data
      })
    }, false, 'setBusinessClientData');
  }

  function setStripeLoadingFailed(stripeLoadingFailed) {
    setState({
      stripeLoadingFailed
    }, false, 'setStripeLoadingFailed');
  }

  function setInAppNotificationsDrawerOpen(inAppNotificationsDrawerOpen) {
    setState({
      inAppNotificationsDrawerOpen
    }, false, 'inAppNotificationsDrawerOpen');

    if (inAppNotificationsDrawerOpen) {
      setState({
        showNotificationPimple: false
      }, false, 'newNotificationsCounter');
    }
  }
  /** Updates the sidebarCounters. You can give it partial data and it will merge it with the previous data. */


  function setSidebarCounters(sidebarCounters) {
    setState({
      sidebarCounters: _objectSpread(_objectSpread({}, getState().sidebarCounters), sidebarCounters)
    }, false, 'setSidebarCounters');
  }
  /** Fetches the counters for the sidebar and saves them in the store. */


  async function fetchSidebarCounters() {
    const businessId = selectors.user.businessId(getState());
    const isVendor = selectors.user.isVendor(getState());

    if (!businessId) {
      return;
    }

    const counters = await api.report.getSidebarCounters(businessId, isVendor);
    setSidebarCounters(counters);
  }

  async function updateBusinessColor(color) {
    const worksAt = selectors.user.business(getState());

    if (worksAt) {
      await api.vendor.updateBusinessDetails(worksAt.id, {
        color
      });
      setWorksAt(Object.assign({}, worksAt, {
        branding: {
          color
        }
      }));
    }
  }

  async function getAndCompareInvoiceSummery() {
    const summary = await api.invoice.getSummariesScout();
    api.invoice.getSummaries().then(mgmtSummary => {
      if (summary.length !== mgmtSummary.length) {
        console.error("summaries do not match in length: ".concat(summary.length, " vs ").concat(mgmtSummary.length));
      }

      mgmtSummary.forEach(invoice => {
        var _invoice$integration, _scoutInvoice$status, _scoutInvoice$integra;

        const scoutInvoice = summary.find(m => m.id === invoice.id) || {};
        const isVendor = selectors.user.isVendor(getState());
        const diff = jsonDiff.diffString(_objectSpread(_objectSpread({}, invoice), {}, {
          status: invoice.status.type,
          integration: isVendor ? {
            status: (_invoice$integration = invoice.integration) === null || _invoice$integration === void 0 ? void 0 : _invoice$integration.status
          } : undefined
        }), _objectSpread(_objectSpread({}, scoutInvoice), {}, {
          status: scoutInvoice === null || scoutInvoice === void 0 ? void 0 : (_scoutInvoice$status = scoutInvoice.status) === null || _scoutInvoice$status === void 0 ? void 0 : _scoutInvoice$status.type,
          integration: isVendor ? {
            status: (_scoutInvoice$integra = scoutInvoice.integration) === null || _scoutInvoice$integra === void 0 ? void 0 : _scoutInvoice$integra.status
          } : undefined
        }), {
          color: false
        });

        if (diff) {
          Sentry.captureException(new Error('invoice summaries do not match'), {
            user: {
              id: selectors.user.id(getState())
            },
            extra: {
              invoiceId: invoice.id,
              relationshipId: invoice.relationship.id,
              isVendor,
              diff
            },
            fingerprint: [JSON.stringify(diff)]
          });
        }
      });
    });
    return summary;
  }

  async function fetchInvoicesAndCounters() {
    let USE_INVOICE_VIEWS = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    requestStart('invoices');
    const invoicesPromise = USE_INVOICE_VIEWS ? getAndCompareInvoiceSummery() : api.invoice.getSummaries();
    requestStart('invoicesCounters');
    const invoicesCountersPromise = api.invoice.getCounters();
    const [[invoices, invoicesErr], [invoicesCounters, invoicesCountersErr]] = await Promise.all([handlePromise(invoicesPromise), handlePromise(invoicesCountersPromise)]);

    if (invoices) {
      requestSuccess('invoices', invoices);
    } else {
      requestFail('invoices', invoicesErr);
    }

    if (invoicesCounters) {
      requestSuccess('invoicesCounters', invoicesCounters);
    } else {
      requestFail('invoicesCounters', invoicesCountersErr);
    }
  }

  async function fetchPayouts() {
    requestStart('payouts');
    const [payouts, err, hasError] = await handleRequest(api.payout.getAll());

    if (hasError) {
      requestFail('payouts', err);
    } else {
      requestSuccess('payouts', payouts);
    }
  }

  async function fetchAgreements() {
    const businessId = selectors.user.businessId(getState());
    const isVendor = selectors.user.isVendor(getState());
    const storeLocation = ['agreements'];

    if (!businessId) {
      return [];
    }

    requestStart(storeLocation);

    try {
      const agreements = await api.relationship.getSummaries({
        businessId,
        type: 'agreement',
        isVendor,
        filters: {
          agreementStatus: defaultAgreementStatusFilter
        }
      });
      requestSuccess(storeLocation, agreements);
      return agreements;
    } catch (err) {
      requestFail(storeLocation, err);
      return [];
    }
  }

  async function fetchContacts() {
    const storeLocation = 'contacts';
    requestStart(storeLocation);

    try {
      const contacts = await api.contact.getAll();
      requestSuccess(storeLocation, contacts);
      return contacts;
    } catch (err) {
      requestFail(storeLocation, err);
      return null;
    }
  }

  async function fetchDrafts() {
    const storeLocation = 'drafts';
    requestStart(storeLocation);

    try {
      const drafts = await api.draft.getSummaries();
      requestSuccess(storeLocation, drafts);
    } catch (err) {
      requestFail(storeLocation, err);
    }
  }

  async function fetchQboCustomers() {
    const storeLocation = 'qboCustomers';
    requestStart(storeLocation);

    try {
      const qboCustomers = await api.qbo.getCustomers();
      requestSuccess(storeLocation, qboCustomers);
      return qboCustomers;
    } catch (err) {
      requestFail(storeLocation, err);
    }
  }

  async function fetchQboItems() {
    const storeLocation = 'qboItems';
    requestStart(storeLocation);

    try {
      const qboItems = await api.qbo.getItems();
      requestSuccess(storeLocation, qboItems);
    } catch (err) {
      requestFail(storeLocation, err);
    }
  }

  async function fetchProposals() {
    const businessId = selectors.user.businessId(getState());
    const isVendor = selectors.user.isVendor(getState());
    const storeLocation = 'proposals';

    if (!businessId) {
      return;
    }

    requestStart(storeLocation);

    try {
      const proposals = await api.relationship.getSummaries({
        businessId,
        type: 'proposal',
        isVendor
      });
      requestSuccess(storeLocation, proposals);
    } catch (err) {
      requestFail(storeLocation, err);
    }
  }

  async function fetchProposalTemplates() {
    const storeLocation = 'proposalTemplates';
    requestStart(storeLocation);

    try {
      const proposalTemplates = await api.proposalTemplates.getAll();
      requestSuccess(storeLocation, proposalTemplates);
    } catch (err) {
      requestFail(storeLocation, err);
    }
  }

  async function fetchMonthlyEarnings() {
    const storeLocation = 'monthlyEarnings';
    requestStart(storeLocation);

    try {
      const monthlyEarnings = await api.report.getMonthlyEarnings();
      requestSuccess(storeLocation, monthlyEarnings);
    } catch (err) {
      requestFail(storeLocation, err);
    }
  }

  async function fetchIntegrations() {
    const storeLocation = 'integrations';
    requestStart(storeLocation);

    try {
      const integrations = await api.integration.getAll();
      requestSuccess(storeLocation, integrations);
      return integrations;
    } catch (err) {
      requestFail(storeLocation, err);
    }
  }

  async function fetchInAppNotifications() {
    requestStart('inAppNotifications');

    try {
      const {
        pushedNotifications,
        counter
      } = await api.inAppNotifications.getAll();
      requestSuccess('inAppNotifications', pushedNotifications);
      const inAppNotificationsDrawerOpen = getState().inAppNotificationsDrawerOpen;
      const shouldShowPimple = !inAppNotificationsDrawerOpen && Boolean(counter);
      setState({
        showNotificationPimple: shouldShowPimple
      }, false, 'newNotificationsCounter');
    } catch (err) {
      requestFail('inAppNotifications', err);
    }
  }

  async function fetchServiceTemplates() {
    const storeLocation = 'serviceTemplates';
    requestStart(storeLocation);

    try {
      const businessId = selectors.user.businessId(getState());
      const {
        active,
        deleted
      } = await api.serviceTemplate.getAll(businessId, {
        includeDeleted: true
      });
      const ret = {
        active,
        deleted
      };
      requestSuccess(storeLocation, ret);
      return ret;
    } catch (err) {
      requestFail(storeLocation, err);
      return null;
    }
  }

  async function fetchUserClientData() {
    const userInfo = getState().userInfo;

    if (!(userInfo !== null && userInfo !== void 0 && userInfo.id)) {
      throw new Error("Can't fetchUserClientData. No userInfo.");
    }

    const data = await api.user.getUserClientData(userInfo.id);
    setUserClientData(data);
    return data;
  }

  async function fetchBusinessClientData(businessId) {
    const bid = businessId || selectors.user.businessId(getState());

    if (!bid) {
      return {};
    }

    const data = await api.user.getBusinessClientData(bid);
    setBusinessClientData(bid, data);
    return data;
  }

  async function fetchLegalTerms() {
    requestStart('legalTerms');
    const [legalTerms, err, hasError] = await handlePromise(api.settings.getLegalTerms());

    if (hasError) {
      requestFail('legalTerms', err);
      return null;
    } else {
      requestSuccess('legalTerms', legalTerms);
      return legalTerms;
    }
  }

  async function updateBusinessClientData(businessId, changes) {
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    const {
      useLocalData
    } = options;
    const currentData = useLocalData ? selectors.user.businessClientData(getState(), businessId) : await fetchBusinessClientData();

    const newData = _objectSpread(_objectSpread({}, currentData), changes);

    await api.user.setBusinessClientData(businessId, newData);
    setBusinessClientData(businessId, newData);
    return newData;
  }

  async function fetchActivityLog(relationshipId) {
    const storeLocation = ['activityLog', relationshipId];
    requestStart(storeLocation);

    try {
      const res = await api.activityLog.get(relationshipId);
      requestSuccess(storeLocation, res);
      return res;
    } catch (err) {
      requestFail(storeLocation, err);
    }
  }

  async function updateUserClientData(changes) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const {
      useLocalData
    } = options;
    const {
      userInfo,
      userClientData
    } = getState();

    if (!(userInfo !== null && userInfo !== void 0 && userInfo.id)) {
      throw new Error("Can't updateUserClientData. No userInfo.");
    }

    const currentData = useLocalData ? userClientData : await fetchUserClientData();

    const newData = _objectSpread(_objectSpread({}, currentData), changes);

    await api.user.setUserClientData(userInfo.id, newData);
    setUserClientData(newData);
    return newData;
  }
  /** Logs a BI event. */


  function logBI(id, data) {
    var _window$debug, _userInfo$worksAt;

    if ((_window$debug = window.debug) !== null && _window$debug !== void 0 && _window$debug.logbi) {
      console.log('BI', id, data);
    }

    const {
      userInfo,
      relationshipScope
    } = getState();
    logBiEvent(id, _objectSpread({
      userId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.id,
      businessId: userInfo === null || userInfo === void 0 ? void 0 : (_userInfo$worksAt = userInfo.worksAt) === null || _userInfo$worksAt === void 0 ? void 0 : _userInfo$worksAt.id,
      relationshipId: relationshipScope && relationshipScope.id,
      isApprovedRelationship: relationshipScope && relationshipScope.isApproved,
      sessionId: window._sessionId,
      viewportHeight: window.innerHeight,
      viewportWidth: window.innerWidth
    }, data || {}));
  }

  return {
    reset,
    resetBusinessData,
    requestStart,
    requestSuccess,
    requestFail,
    setAuthToken,
    fetchUserInfo,
    setUserInfo,
    setWorksAt,
    setUserClientData,
    setStripeLoadingFailed,
    setInAppNotificationsDrawerOpen,
    setSidebarCounters,
    fetchSidebarCounters,
    updateBusinessColor,
    fetchInvoicesAndCounters,
    fetchPayouts,
    fetchAgreements,
    fetchContacts,
    fetchDrafts,
    fetchQboCustomers,
    fetchQboItems,
    fetchProposals,
    fetchProposalTemplates,
    fetchMonthlyEarnings,
    fetchIntegrations,
    fetchInAppNotifications,
    fetchServiceTemplates,
    fetchUserClientData,
    fetchBusinessClientData,
    fetchActivityLog,
    fetchLegalTerms,
    updateBusinessClientData,
    updateUserClientData,
    sendRequest,
    logBI,
    setRelationshipScope,
    ff: {
      setRelationshipVendorId,
      fetchFeatureFlags
    },
    api
  };
};